import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { formatUkDate } from "../../utils/dateUtils";

interface Statement {
  id: string;
  driverId: string;
  periodStart: string;
  periodEnd: string;
  totalTrips: number;
  totalAmount: number;
  status: string;
}

export default function Statements() {
  const [items, setItems] = useState<Statement[]>([]);
  const [drivers, setDrivers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axiosInstance.get("/statements"),
      axiosInstance.get("/drivers"),
    ]).then(([s, d]) => {
      setItems(s.data);
      setDrivers(Object.fromEntries(d.data.map((x: { id: string; fullName: string }) => [x.id, x.fullName])));
    }).finally(() => setLoading(false));
  }, []);

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete statement?")) return;
    await axiosInstance.delete(`/statements/${id}`);
    setItems((prev) => prev.filter((x) => x.id !== id));
  };

  return (
    <div className="space-y-6">
      <PageBreadcrumb pageTitle="Statements" />
      <div className="flex justify-end mt-6 mb-4">
        <Link to="/statements/add" className="px-4 py-2 bg-brand-500 text-white rounded-lg">Add Statement</Link>
      </div>
      <div className="rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 dark:divide-gray-800">
          <thead className="bg-gray-50 dark:bg-white/[0.02]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trips</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-6 py-4">Loading...</td></tr>
            ) : items.map((row) => (
              <tr key={row.id}>
                <td className="px-6 py-4">{drivers[row.driverId] || row.driverId}</td>
                <td className="px-6 py-4">{formatUkDate(row.periodStart)} – {formatUkDate(row.periodEnd)}</td>
                <td className="px-6 py-4">{row.totalTrips}</td>
                <td className="px-6 py-4">£{Number(row.totalAmount).toFixed(2)}</td>
                <td className="px-6 py-4">{row.status}</td>
                <td className="px-5 py-4 text-end">
                      <div className="flex items-center justify-end gap-3">
                        <Link to={`/statements/edit/${row.id}`} className="text-blue-600">Edit</Link>
                  <button type="button" onClick={() => handleDelete(row.id)} className="text-red-600">Delete</button>
                      </div>
                    </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
